import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { toast } from '../../../../../ui/toast';
import { CircleCheckIcons, ClipBoardIcons, ListCheckIcons, NewUserIcons, ShieldAlertIcons, StarIcons, UsersIcons } from '../../../../../icons/icons';
import { getApplicantCsv, getFilter, getJobDescription, sendUnsuccessfulEmail, twoDJobApplicants, updateJobApplicantStatus } from '../../../../../utils/api_service/JobOpenings';
import { JobOpeningsContext } from '../../../../../utils/contexts/JobOpening';
import { RootState } from '../../../../../utils/redux_store/store';
import {
  Applicant,
  ApplicantResponse,
  JobApplicantCSV,
  JobApplicantSearchRequest,
  PreQualFilter,
  Skill,
  TabData,
} from '../../../../../utils/types/JobOpenings';
import { GetColumns } from '../Functions/GetColumns';

// Utility function to normalize status display
const normalizeStatusDisplay = (status: string): string => {
  if (status === 'Unsuccessful-Closed' || status === 'Unsuccessful-Pending') {
    return 'Unsuccessful';
  }
  return status;
};

// Normalize inbound filter data (backend may return camelCase or snake_case keys)
const normalizeSearchRequest = (data: any): JobApplicantSearchRequest => ({
  additional_skills: {
    skill_weightage:
      data.additional_skills?.skill_weightage ??
      data.additionalSkills?.skillWeightage ??
      false,
    similar:
      data.additional_skills?.similar ??
      data.additionalSkills?.similar ??
      false,
    skills:
      data.additional_skills?.skills ??
      data.additionalSkills?.skills ??
      [],
  },
  job_title: {
    similar:
      data.job_title?.similar ??
      data.jobTitle?.similar ??
      false,
    job_titles:
      data.job_title?.job_titles ??
      data.jobTitle?.jobTitles ??
      [],
  },
  keywords: {
    keyword_weightage:
      data.keywords?.keyword_weightage ??
      data.keywords?.keywordWeightage ??
      false,
    similar: data.keywords?.similar ?? false,
    keywords: data.keywords?.keywords ?? [],
  },
  behavioral_attributes: {
    similar:
      data.behavioral_attributes?.similar ??
      data.behavioralAttributes?.similar ??
      false,
    behavioral_attributes:
      data.behavioral_attributes?.behavioral_attributes ??
      data.behavioralAttributes?.behavioralAttributes ??
      [],
  },
  gender: data.gender ?? [],
  age: {
    min_age: data.age?.min_age ?? data.age?.minAge ?? 0,
    max_age: data.age?.max_age ?? data.age?.maxAge ?? 100,
    include_unknown: data.age?.include_unknown ?? data.age?.includeUnknown ?? true,
  },
  applicant_status: data.applicant_status ?? data.applicantStatus ?? null,
  prequal_filter: data.prequal_filter ?? null,
});
const isLicenseActive = (feature?: { startDate: number | string; endDate: number | string }): boolean => {
  if (!feature) return false;
  return Number(feature.endDate) * 1000 > Date.now();
};

export const useApplicantAnalysis = () => {
  const { subscriptions } = useSelector((state: RootState) => state.subscriptions);
  const hasCCALicense = isLicenseActive(subscriptions?.hierTalentCca);

  const [tabTitles, setTabTitles] = useState<string[]>(
    [
      'Overview',
      'All Applicants',
      ...(hasCCALicense ? ['CCA Results'] : []),
    ],
  );
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState<number>(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const tab = searchParams.get('applicantTab');
    const tabIndex = parseInt(tab || '1');
    return isNaN(tabIndex) ? 1 : tabIndex;
  });
  const [selectedCandidates, setSelectedCandidates] = useState<TabData[]>([]);
  const [extractedSkill, setExtractedSkill] = useState<Skill[]>([]);
  const [extractedJobTitles, setExtractedJobTitles] = useState<Skill[]>([]);
  const [extractedBehavioralScore, setExtractedBehavioralScore] = useState<
    Skill[]
  >([]);
  const [extractedKeywords, setExtractedKeywords] = useState<Skill[]>([]);
  const { jobOpeningElements } = useContext(JobOpeningsContext);
  const [currentTab, setCurrentTab] = useState<string>(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const urlStatus = searchParams.get('status');
    if (!urlStatus) return 'All Applicants';
    return (
      urlStatus
        .split('-')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ') || 'All Applicants'
    );
  });

  // Get applicant tab from URL
  const getApplicantTabFromUrl = (): number => {
    const searchParams = new URLSearchParams(location.search);
    const tab = searchParams.get('applicantTab');
    const tabIndex = parseInt(tab || '1');
    return isNaN(tabIndex) ? 1 : tabIndex;
  };

  // Get current tab status from URL
  const getCurrentTabFromUrl = (): string => {
    const searchParams = new URLSearchParams(location.search);
    const urlStatus = searchParams.get('status');
    // Convert from URL format back to display format
    return convertFromUrlFormat(urlStatus) || 'All Applicants';
  };

  // Helper function to convert tab labels to URL-safe format
  const convertToUrlFormat = (tabLabel: string): string => {
    return tabLabel.toLowerCase().replace(/\s+/g, '-');
  };

  // Helper function to convert from URL format back to display format
  const convertFromUrlFormat = (urlValue: string | null): string | null => {
    if (!urlValue) return null;
    return urlValue
      .split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Update URL when applicant tab changes
  const updateActiveTab = (tabIndex: number) => {
    setActiveTab(tabIndex);
    const searchParams = new URLSearchParams(window.location.search);

    // Always ensure main tab is set correctly for Applicant Analysis
    searchParams.set('tab', 'applicant-analysis');
    searchParams.set('applicantTab', tabIndex.toString());

    // Preserve current status tab if it exists
    if (currentTab && currentTab !== 'All Applicants') {
      searchParams.set('status', convertToUrlFormat(currentTab));
    } else {
      searchParams.delete('status');
    }

    const newUrl = `${window.location.pathname}?${searchParams.toString()}`;
    window.history.pushState(null, '', newUrl);
  };

  // Sync state with URL on location change - only when URL actually changes
  // Previously depended on activeTab/currentTab causing extra renders and chaining updates
  useEffect(() => {
    const tabFromUrl = getApplicantTabFromUrl();
    const statusFromUrl = getCurrentTabFromUrl();

    setActiveTab((prev) => (prev !== tabFromUrl ? tabFromUrl : prev));
    setCurrentTab((prev) => (prev !== statusFromUrl ? statusFromUrl : prev));
  }, [location.search]);

  // Listen to browser back/forward navigation
  useEffect(() => {
    const handlePopState = () => {
      const tabFromUrl = getApplicantTabFromUrl();
      const statusFromUrl = getCurrentTabFromUrl();
      setActiveTab((prev) => (prev !== tabFromUrl ? tabFromUrl : prev));
      setCurrentTab((prev) => (prev !== statusFromUrl ? statusFromUrl : prev));
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);
  const [viewMode, setViewMode] = useState<'table' | 'grid'>('grid');
  const [showHeadshots, setShowHeadshots] = useState<boolean>(false);
  const [status, setStatus] = useState<string[]>([
    'New',
    'Suitable',
    'Outstanding',
    'ShortList',
    'Unsuccessful',
    'Auto Assess',
  ]);
  const [statusValue, setStatusValue] = useState<string[]>([]);
  const [similarKeyword, setSimilarKeyword] = useState<string>('');
  const [projection, setProjection] = useState<string>('perspective');
  const [boxWidthDepth, setBoxWidthDepth] = useState<number[]>([200, 80]);
  const [maxLegion, setMaxLegion] = useState<number>(10);
  const [exactKeyword, setExactKeyword] = useState<string>('');
  const [skillsInput, setSkillsInput] = useState<string>('');
  const [behavioralAttr, setBehavioralAttr] = useState<string>('');
  const [jobTitleInput, setJobTitleInput] = useState<string>('');
  const [defaultSkills, setDefaultSkills] = useState<string[]>([]);
  const [defaultSkillsCol, setDefaultSkillsCol] = useState<string[]>([]);
  const { id } = useParams<{ id: string }>();
  const [view3D, setView3D] = useState<boolean>(false);
  const [skillValue, setSkillValue] = useState<number[]>([0, 10]);
  const [experienceValue, setExperienceValue] = useState<number[]>([0, 10]);
  const [candidates, setCandidates] = useState<Applicant[]>([]);
  const [filteredCandidates, setFilteredCandidates] = useState<Applicant[]>([]);
  const [globalSearchTerm, setGlobalSearchTerm] = useState<string>('');
  const [showSearchPanel, setShowSearchPanel] = useState<boolean>(false);
  const [selectedApplicantsForEmail, setSelectedApplicantsForEmail] = useState<
    Applicant[]
  >([]);
  const [minMaxAge, setMinMaxAge] = useState<number[]>([0, 100]);
  const [loading, setLoading] = useState<boolean>(true);
  const [weightingEnabled, setWeightingEnabled] = useState<boolean>(false);
  const [keywordWeightage, setKeywordWeightage] = useState<boolean>(true);
  const [scatter, setScatter] = useState<boolean>(true);
  const [ageToggle, setAgeToggle] = useState<boolean>(true);
  const [useSkillPercentage, setUseSkillPercentage] = useState<boolean>(false);
  const [similarToggle, setSimilarToggle] = useState<boolean>(false);
  const [similarBehavior, setSimilarBehavior] = useState<boolean>(false);
  const [keywordToggle, setKeywordToggle] = useState<boolean>(false);
  const [similarJobTitle, setSimilarJobTitle] = useState<boolean>(false);
  const [skillLength, setSkillLength] = useState<number>(10);
  const [svgActiveTab, setSvgActiveTab] = useState<number>(-1);
  const [genderList, setGenderList] = useState<string[]>([]);
  const [statusList, setStatusList] = useState<string[]>([]);
  const [keywordList, setKeywordList] = useState<string[]>([]);
  const [isColumnConfigurationOpen, setIsColumnConfigurationOpen] =
    useState(false);
  const [isAnalyticalFilter, setIsAnalyticalFilter] = useState(false);
  const [isAnalytical3D, setIsAnalytical3D] = useState(false);
  const [isToolbarExpanded, setIsToolbarExpanded] = useState(true);
  const [prequal_filter, setPrequalFilter] =
    useState<PreQualFilter | null>(null);
  const { columns, setColumns, getColumns } = GetColumns();
  // Gate 2d-graph fetching until initial applicant filters are loaded.
  // Prevents premature fetch with default searchObject that causes double call.
  const filtersReadyRef = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchObjectRef = useRef<JobApplicantSearchRequest | null>(null);
  const [markLines, setMarkLines] = useState<boolean>(true);
  const [nineBox, setNineBox] = useState<boolean>(true);
  const [isSeriesModal, setIsSeriesModal] = useState<boolean>(false);
  const [colorList, setColorList] = useState<string[]>(['#1D4ED8', '#B91C1C']);
  const [seriesCandidates, setSeriesCandidates] = useState<Applicant[]>([]);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState<boolean>(false);
  const [profileModalCandidates, setProfileModalCandidates] = useState<
    Applicant[]
  >([]);
  const [profileModalInitialIndex, setProfileModalInitialIndex] =
    useState<number>(0);
  const [profileModalTileName, setProfileModalTileName] = useState<string>('');
  const [isEmailConfirmationModalOpen, setIsEmailConfirmationModalOpen] =
    useState<boolean>(false);
  const [isEmailSending, setIsEmailSending] = useState<boolean>(false);
  const [searchObject, setSearchObject] = useState<JobApplicantSearchRequest>({
    additional_skills: {
      skill_weightage: false,
      similar: false,
      skills: [],
    },
    job_title: {
      similar: false,
      job_titles: jobTitleInput ? [jobTitleInput] : [],
    },
    keywords: {
      keyword_weightage: false,
      similar: false,
      keywords: [],
    },
    behavioral_attributes: {
      similar: false,
      behavioral_attributes: [],
    },
    gender: [],
    age: {
      min_age: 0,
      max_age: minMaxAge[1],
      include_unknown: ageToggle,
    },
    applicant_status: statusValue,
    prequal_filter: prequal_filter,
  });

  const [searchCSVObject, setSearchCSVObject] = useState<JobApplicantCSV>({
    additional_skills: {
      skill_weightage: false,
      similar: false,
      skills: [],
    },
    job_title: {
      similar: false,
      job_titles: jobTitleInput ? [jobTitleInput] : [],
    },
    keywords: {
      keyword_weightage: false,
      similar: false,
      keywords: [],
    },
    gender: [],
    age: {
      min_age: 0,
      max_age: minMaxAge[1],
      include_unknown: ageToggle,
    },
    applicant_status: statusValue,
    skills: {
      min: 0,
      max: 10,
    },
    experience: {
      min: 0,
      max: 10,
    },
    behavioral_attributes: {
      similar: false,
      behavioral_attributes: [],
    },
    prequal_filter: prequal_filter,
  });

  // Keep ref in sync for stable fetchJobApplicants without stale closure
  useEffect(() => {
    searchObjectRef.current = searchObject;
  }, [searchObject]);

  const handleExportClick = async () => {
    const response = await getApplicantCsv(id, searchCSVObject);
    const responseData = await response.text();
    const blob = new Blob([responseData], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'applicants.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  // Function to send unsuccessful email to single applicant (for profile dialog)
  const handleSendEmailToSingleApplicant = async (
    applicant: Applicant,
    updateStatusFirst: boolean = true,
  ) => {
    if (!applicant.email || applicant.email.trim() === '') {
      toast.error('No email address found for this applicant', {
        position: 'bottom-center',
      });
      return;
    }

    const isUnsuccessful =
      applicant.applicantStatus &&
      (applicant.applicantStatus.includes('Unsuccessful') ||
        applicant.applicantStatus === 'Unsuccessful-Pending' ||
        applicant.applicantStatus === 'Unsuccessful-Closed');

    if (!isUnsuccessful) {
      toast.error(
        'Can only send unsuccessful emails to unsuccessful applicants',
        {
          position: 'bottom-center',
        },
      );
      return;
    }

    try {
      // Send email
      const emailData = {
        email: applicant.email,
      };
      await sendUnsuccessfulEmail(id!, emailData);

      // Refresh the applicants list to show updated statuses
      await fetchJobApplicants();

      const statusMessage = updateStatusFirst
        ? 'Unsuccessful email sent (status updated to closed)'
        : 'Unsuccessful email sent (status unchanged)';

      toast.success(statusMessage, {
        position: 'bottom-center',
      });
    } catch (error) {
      toast.error('Failed to send unsuccessful email', {
        position: 'bottom-center',
      });
    }
  };

  const handleSendEmailToSelected = async () => {
    if (selectedApplicantsForEmail.length === 0) {
      toast.error('Please select applicants to send emails to', {
        position: 'bottom-center',
      });
      return;
    }

    // Filter to get only unsuccessful-pending applicants with valid email addresses
    const pendingApplicants = selectedApplicantsForEmail.filter((candidate) => {
      const hasEmail = candidate.email && candidate.email.trim() !== '';
      const isPending = candidate.applicantStatus === 'Unsuccessful-Pending';
      return hasEmail && isPending;
    });

    if (pendingApplicants.length === 0) {
      toast.error(
        'No unsuccessful-pending applicants with email addresses found in selection',
        {
          position: 'bottom-center',
        },
      );
      return;
    }

    // Show confirmation modal
    setIsEmailConfirmationModalOpen(true);
  };

  const handleConfirmSendEmail = async () => {
    if (selectedApplicantsForEmail.length === 0) {
      return;
    }

    // Filter to get only unsuccessful-pending applicants with valid email addresses
    const pendingApplicants = selectedApplicantsForEmail.filter((candidate) => {
      const hasEmail = candidate.email && candidate.email.trim() !== '';
      const isPending = candidate.applicantStatus === 'Unsuccessful-Pending';
      return hasEmail && isPending;
    });

    if (pendingApplicants.length === 0) {
      setIsEmailConfirmationModalOpen(false);
      return;
    }

    setIsEmailSending(true);

    try {
      // FIRST: Update status to Unsuccessful-Closed before sending emails
      const statusUpdatePromises = pendingApplicants.map(async (applicant) => {
        const statusUpdateData = {
          jobApplicantIds: [applicant.userId],
          applicantStatus: 'Unsuccessful-Closed',
        };

        const response = await updateJobApplicantStatus(id!, statusUpdateData);
        if (!response.ok) {
          throw new Error(
            `Failed to update status for applicant ${applicant.userId}`,
          );
        }
        return response;
      });

      await Promise.all(statusUpdatePromises);

      // THEN: Send emails to each candidate (now with closed status)
      const emailPromises = pendingApplicants.map(async (applicant) => {
        const emailData = {
          email: applicant.email!,
        };
        return sendUnsuccessfulEmail(id!, emailData);
      });

      await Promise.all(emailPromises);

      // Refresh the applicants list to show updated statuses
      await fetchJobApplicants();

      toast.success(
        `Unsuccessful emails sent to ${pendingApplicants.length} selected applicants`,
        {
          position: 'bottom-center',
        },
      );

      // Clear selection after sending
      setSelectedApplicantsForEmail([]);

      // Close the confirmation modal
      setIsEmailConfirmationModalOpen(false);
    } catch (error) {
      toast.error('Failed to send unsuccessful emails or update status', {
        position: 'bottom-center',
      });
    } finally {
      setIsEmailSending(false);
    }
  };

  const handleCloseEmailConfirmationModal = () => {
    if (!isEmailSending) {
      setIsEmailConfirmationModalOpen(false);
    }
  };

  const handleApplicantSelection = (
    applicant: Applicant,
    isSelected?: boolean,
  ) => {
    // If isSelected is not provided (e.g., from chart click), toggle selection
    if (isSelected === undefined) {
      const currentlySelected = selectedApplicantsForEmail.some(
        (a) => a.userId === applicant.userId,
      );
      if (currentlySelected) {
        setSelectedApplicantsForEmail((prev) =>
          prev.filter((a) => a.userId !== applicant.userId),
        );
      } else {
        setSelectedApplicantsForEmail((prev) => [...prev, applicant]);
      }
    } else {
      // Direct selection/deselection
      if (isSelected) {
        setSelectedApplicantsForEmail((prev) => {
          const exists = prev.some((a) => a.userId === applicant.userId);
          return exists ? prev : [...prev, applicant];
        });
      } else {
        setSelectedApplicantsForEmail((prev) =>
          prev.filter((a) => a.userId !== applicant.userId),
        );
      }
    }
  };

  const isApplicantSelected = (applicant: Applicant) => {
    return selectedApplicantsForEmail.some(
      (a) => a.userId === applicant.userId,
    );
  };

  const selectAllUnsuccessful = () => {
    // Only select Unsuccessful-Pending candidates, exclude Unsuccessful-Closed
    let pendingUnsuccessfulApplicants;

    if (currentTab === 'Unsuccessful Applicants') {
      // On the unsuccessful tab, filter for only pending candidates
      pendingUnsuccessfulApplicants = filteredCandidates.filter(
        (candidate) => candidate.applicantStatus === 'Unsuccessful-Pending',
      );
    } else {
      // On other tabs, filter for only pending unsuccessful candidates
      pendingUnsuccessfulApplicants = filteredCandidates.filter(
        (candidate) => candidate.applicantStatus === 'Unsuccessful-Pending',
      );
    }

    setSelectedApplicantsForEmail(pendingUnsuccessfulApplicants);
  };

  const clearSelection = () => {
    setSelectedApplicantsForEmail([]);
  };

  // Function to handle moving candidates to unsuccessful status
  const handleMoveToUnsuccessful = async (candidates: Applicant[]) => {
    try {
      const candidateIds = candidates.map((c) => c.userId);
      const statusUpdateData = {
        jobApplicantIds: candidateIds,
        applicantStatus: 'Unsuccessful-Pending', // Set to pending instead of closed
      };

      const response = await updateJobApplicantStatus(id!, statusUpdateData);
      if (!response.ok) {
        toast.error('Failed to update candidate status', {
          position: 'bottom-center',
        });
        return;
      }

      // Refresh the applicants list to show updated statuses
      await fetchJobApplicants();

      toast.success(
        `${candidateIds.length} candidate(s) moved to unsuccessful pending status`,
        {
          position: 'bottom-center',
        },
      );
    } catch (error) {
      toast.error('Failed to update candidate status', {
        position: 'bottom-center',
      });
    }
  };

  // Function to handle moving candidates from open to unsuccessful pending
  const handleMoveToUnsuccessfulFromOpen = async (candidates: Applicant[]) => {
    try {
      const candidateIds = candidates.map((c) => c.userId);
      const statusUpdateData = {
        jobApplicantIds: candidateIds,
        applicantStatus: 'Unsuccessful-Pending', // Move from open to unsuccessful pending
      };

      const response = await updateJobApplicantStatus(id!, statusUpdateData);
      if (!response.ok) {
        toast.error('Failed to update candidate status', {
          position: 'bottom-center',
        });
        return;
      }

      // Refresh the applicants list to show updated statuses
      await fetchJobApplicants();

      toast.success(
        `${candidateIds.length} candidate(s) moved from open to unsuccessful pending status`,
        {
          position: 'bottom-center',
        },
      );
    } catch (error) {
      toast.error('Failed to update candidate status', {
        position: 'bottom-center',
      });
    }
  };

  const handleBulkStatusChange = async (
    selectedApplicants: Applicant[],
    targetStatus: string,
  ) => {
    if (!selectedApplicants || selectedApplicants.length === 0) {
      toast.error('No applicants selected', {
        position: 'bottom-center',
      });
      return;
    }

    try {
      const candidateIds = selectedApplicants.map((c) => c.userId);
      const statusUpdateData = {
        jobApplicantIds: candidateIds,
        applicantStatus: targetStatus,
      };

      const response = await updateJobApplicantStatus(id!, statusUpdateData);
      if (!response.ok) {
        toast.error('Failed to update candidate status', {
          position: 'bottom-center',
        });
        return;
      }
      await fetchJobApplicants();

      toast.success(
        `${candidateIds.length} candidate(s) moved to ${targetStatus}`,
        {
          position: 'bottom-center',
        },
      );
      setSelectedApplicantsForEmail([]);
    } catch (error) {
      toast.error('Failed to update candidate status', {
        position: 'bottom-center',
      });
    }
  };

  const handleOpenCandidateTab = useCallback(
    (applicant: Applicant) => {
      if (!applicant || !applicant.userId) {
        console.error(
          'handleOpenCandidateTab called with invalid applicant:',
          applicant,
        );
        return;
      }

      const fullName =
        applicant.name || `${applicant.firstName} ${applicant.lastName}`;

      // Use functional state updates to access the absolute latest state snapshots
      // and prevent race conditions between rapid tab opening clicks.

      setSelectedCandidates((prevSelected) => {
        // Find if this candidate already has a tab
        const existingIndex = prevSelected.findIndex(
          (item) =>
            item.candidate && item.candidate.userId === applicant.userId,
        );

        if (existingIndex !== -1) {
          // Candidate already has a tab, switch to it
          const tabId = prevSelected[existingIndex].tabId;
          updateActiveTab(tabId);
          return prevSelected;
        }

        // Insert BEFORE 'CCA Results' if it's the last tab, so CCA always stays last.
        const lastIsCCA = tabTitles[tabTitles.length - 1] === 'CCA Results';
        const newTabIndex = lastIsCCA ? tabTitles.length - 1 : tabTitles.length;

        // Update titles in lockstep, keeping CCA Results as the final tab
        setTabTitles((prevTitles) => {
          const lastTitle = prevTitles[prevTitles.length - 1];
          if (lastTitle === 'CCA Results') {
            return [...prevTitles.slice(0, -1), fullName, 'CCA Results'];
          }
          return [...prevTitles, fullName];
        });

        // Switch to the new tab
        setTimeout(() => {
          updateActiveTab(newTabIndex);
        }, 0);

        return [...prevSelected, { tabId: newTabIndex, candidate: applicant }];
      });

      // Safety: close any modals if open
      setIsProfileModalOpen(false);
      setIsSeriesModal(false);
    },
    [updateActiveTab],
  );

  const handleCloseTab = useCallback(
    (index: number) => {
      if (index < 2) return; // Don't close Overview or All Applicants

      // Update titles and records in parallel functional updaters
      // Titles update first to maintain UI consistency
      setTabTitles((prevTitles) => prevTitles.filter((_, i) => i !== index));

      // Update candidates and shift subsequent tabIds to match the title array shift
      setSelectedCandidates((prevSelected) => {
        return prevSelected
          .filter((item) => item.tabId !== index)
          .map((item) => {
            if (item.tabId > index) {
              return { ...item, tabId: item.tabId - 1 };
            }
            return item;
          });
      });

      // Adjust active tab if necessary
      if (activeTab === index) {
        updateActiveTab(Math.max(1, index - 1));
      } else if (activeTab > index) {
        updateActiveTab(activeTab - 1);
      }
    },
    [activeTab, updateActiveTab],
  );

  const handleViewApplicantProfile = (
    applicant: Applicant,
    tileCandidates?: Applicant[],
    tileName?: string,
  ) => {
    const candidatesToShow = tileCandidates || filteredCandidates;
    const candidateIndex = candidatesToShow.findIndex(
      (c) => c.userId === applicant.userId,
    );
    setProfileModalCandidates(candidatesToShow);
    setProfileModalInitialIndex(candidateIndex !== -1 ? candidateIndex : 0);
    setProfileModalTileName(tileName || '');
    setIsProfileModalOpen(true);
  };

  const handleCandidateClickFromChart = (
    candidate: Applicant,
    tileCandidates?: Applicant[],
    tileName?: string,
  ) => {
    if (tileCandidates && tileCandidates.length > 1) {
      // Restore Triage View for multiple candidates
      setProfileModalCandidates(tileCandidates);
      const index = tileCandidates.findIndex(
        (c) =>
          (c.userId || (c as any).id) ===
          (candidate.userId || (candidate as any).id),
      );
      setProfileModalInitialIndex(index !== -1 ? index : 0);
      setProfileModalTileName(tileName || 'Triage View');
      setIsProfileModalOpen(true);
    } else {
      // Single candidate opening in tab
      handleOpenCandidateTab(candidate);
    }
  };

  const getApplicantInitials = (applicant: Applicant) => {
    const fullName =
      applicant.name || `${applicant.firstName} ${applicant.lastName}`;
    const nameParts = fullName.split(' ').filter(Boolean);
    if (nameParts.length >= 2) {
      return (
        nameParts[0][0].toUpperCase() +
        nameParts[nameParts.length - 1][0].toUpperCase()
      );
    } else if (nameParts.length === 1) {
      return nameParts[0][0].toUpperCase();
    }
    return 'U';
  };

  const fetchJobApplicants = useCallback(async (signal?: AbortSignal, overrideSearchObject?: JobApplicantSearchRequest) => {
    if (!id) return;
    // Use override if provided (from debounced effect), else latest ref or closure
    const currentSearchObject = overrideSearchObject ?? searchObjectRef.current ?? searchObject;
    // Update searchObject to include skillWeightage based on useSkillPercentage
    const updatedSearchObject: JobApplicantSearchRequest = {
      ...currentSearchObject,
      additional_skills: {
        ...currentSearchObject.additional_skills,
        skill_weightage: useSkillPercentage,
      },
    };
    // Sanitize empty string filters ([''] should be [] else backend returns 0 matches)
    if (updatedSearchObject.job_title?.job_titles) {
      updatedSearchObject.job_title.job_titles = updatedSearchObject.job_title.job_titles.filter((v: string) => v && v.trim() !== '');
    }
    if ((updatedSearchObject as any).behavioral_attributes?.behavioral_attributes) {
      (updatedSearchObject as any).behavioral_attributes.behavioral_attributes = (updatedSearchObject as any).behavioral_attributes.behavioral_attributes.filter((v: string) => v && v.trim() !== '');
    }

    console.log('[2d-graph] request', { id, searchObject: updatedSearchObject });
    const response = await twoDJobApplicants(id, updatedSearchObject, signal);
    if (signal?.aborted) return;
    const responseData = await response.json();
    if (signal?.aborted) return;
    const getInitials = (name: string) => {
      const nameParts = name.split(' ').filter(Boolean);
      const initials = nameParts.map((part) => part[0].toUpperCase()).join('');
      return initials ? initials.slice(0, 2) : 'U';
    };
    const transformApplicant = (item: ApplicantResponse): Applicant => {
      let computedPercentage = item.skillPercentage || 0;
      if (
        useSkillPercentage &&
        (!computedPercentage || computedPercentage === 0) &&
        updatedSearchObject.additional_skills?.skills?.length > 0
      ) {
        const allSkills = updatedSearchObject.additional_skills.skills;
        const totalWeight = allSkills.reduce((sum: number, s: any) => sum + (Number(s.weight) || 0), 0);
        if (totalWeight > 0 && item.matchedSkills && item.matchedSkills.length > 0) {
          const matchedWeight = item.matchedSkills.reduce((sum: number, matchedSkill: string) => {
            const skillObj = allSkills.find((s: any) => s.skill.toLowerCase() === matchedSkill.toLowerCase());
            return sum + (skillObj ? Number(skillObj.weight) || 0 : 0);
          }, 0);
          computedPercentage = Number(((matchedWeight / totalWeight) * 100).toFixed(2));
        }
      }
      return {
        userId: item.id,
        value: useSkillPercentage
          ? [computedPercentage, item.experience, item.behaviorCount, item.keywordCount]
          : [item.skillCount, item.experience, item.behaviorCount, item.keywordCount],
        firstName: item.firstName !== null ? item.firstName : 'undefined',
        lastName: item.lastName !== null ? item.lastName : 'undefined',
        age: item.age,
        gender: item.gender,
        aiScore: item.aiScore,
        applicantStatus: item.applicantStatus,
        locationCity: item.locationCity,
        locationCountry: item.locationCountry,
        name: item.firstName && item.lastName !== null ? `${item.firstName} ${item.lastName}` : 'undefined',
        skills: item.skills,
        matchedSkills: item.matchedSkills,
        behavioralAttributes: item.behavioralAttributes,
        matchedBehavior: item.matchedBehavior,
        behavior: item.behavior,
        keywords: item.keyword,
        email: item.email,
        symbol: item.base64Encoded ? `image://${item.base64Encoded}` : 'circle',
        label: { show: item.base64Encoded ? false : true, formatter: getInitials(item.firstName && item.lastName !== null ? `${item.firstName} ${item.lastName}` : 'undefined'), color: 'yellow', fontWeight: 'bold' },
      };
    };
    const highestExperienceCount = responseData.data.reduce((max: any, applicant: any) => Math.max(max, applicant.experience || 0), 0);
    const highestSkillCount = responseData.data.reduce((max: any, applicant: any) => Math.max(max, applicant.skillCount || 0), 0);
    const highestKeywordCount = responseData.data.reduce((max: any, applicant: any) => Math.max(max, applicant.keywordCount || 0), 0);
    const newExperienceCount = highestExperienceCount > 0 ? highestExperienceCount : 10;
    const newKeywordCount = highestKeywordCount > 0 ? highestKeywordCount : 10;
    setMaxLegion(newKeywordCount);
    setExperienceValue([experienceValue[0], newExperienceCount]);
    if (useSkillPercentage) {
      setSkillValue([skillValue[0], 100]);
    } else {
      const jobSkillsList: string[] = currentSearchObject.additional_skills.skills.map((s: { skill: string; weight: number }) => s.skill);
      setSkillValue([skillValue[0], jobSkillsList.length === 0 ? 10 : jobSkillsList.length]);
      setUseSkillPercentage(false);
    }
    const transformedData: Applicant[] = responseData.data.map(transformApplicant);
    setCandidates(transformedData);
    setFilteredCandidates(transformedData);
  }, [id, searchObject, useSkillPercentage]);

  useEffect(() => {
    // Gated fetching: only after initial filter load, with debounce + abort
    // This ensures the correct flow: getFilter -> setSearchObject -> 2d-graph (single call)
    // Previously prequal_filter's initial mount setSearchObject caused an extra 2d-graph before filters loaded.
    if (!filtersReadyRef.current) {
      return;
    }

    // Debounce rapid successive searchObject changes (e.g., tab switch + filter apply) into one request
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Capture current searchObject for the debounced call (avoid stale ref)
    const capturedSearchObject = searchObject;
    debounceTimerRef.current = setTimeout(async () => {
      // Abort any in-flight 2d-graph request (smoothness + avoid stale data)
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      const controller = new AbortController();
      abortControllerRef.current = controller;

      setLoading(true);
      try {
        await fetchJobApplicants(controller.signal, capturedSearchObject);
      } catch (error: any) {
        if (error?.name === 'AbortError' || controller.signal.aborted) {
          return;
        }
        toast.error('Error Loading Job Applicants', {
          position: 'bottom-center',
        });
        setCandidates([]);
        setFilteredCandidates([]);
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    }, 60);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchObject, fetchJobApplicants]);

  // Cleanup abort on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    setSelectedCandidates((prevSelected) =>
      prevSelected.reduce<typeof prevSelected>(
        (acc, item) => {
          if (!item.candidate) return acc;
          const candidateName =
            item.candidate.name ||
            `${item.candidate.firstName} ${item.candidate.lastName}`;
          const tabIndex = tabTitles.findIndex((title) => title === candidateName);
          if (tabIndex === -1) return acc; // tab was closed/removed
          acc.push({ ...item, tabId: tabIndex });
          return acc;
        },
        [],
      ),
    );
  }, [tabTitles]); // eslint-disable-line

  // Global search effect - filter candidates based on search term
  useEffect(() => {
    if (!globalSearchTerm.trim()) {
      setFilteredCandidates(candidates);
      setShowSearchPanel(false);
    } else {
      const searchLower = globalSearchTerm.toLowerCase();
      const filtered = candidates.filter((candidate) => {
        const fullName =
          candidate.name || `${candidate.firstName} ${candidate.lastName}`;
        const matchesName = fullName.toLowerCase().includes(searchLower);
        const matchesEmail =
          candidate.email?.toLowerCase().includes(searchLower) || false;

        // Only search by name or email
        return matchesName || matchesEmail;
      });
      setFilteredCandidates(filtered);
      setShowSearchPanel(true);
    }
  }, [candidates, globalSearchTerm]);

  // Add aiScoreWeights state to hold the value
  const [aiScoreWeights, setAiScoreWeights] = useState<any>({});

  // In your useEffect where getFilters is called:

  useEffect(() => {
    const fetchJobDescriptionDetails = async () => {
      try {
        const response = await getJobDescription(id);
        if (!response.ok) {
          throw new Error('Failed to fetch job description');
        }

        const responseData = await response.json();
        const mandatorySkills = responseData.mandatorySkills;
        const mandatorySkillsString = mandatorySkills.join(':');
        const jobTitle = responseData.jobTitle.jobTitle;
        const defaultBehavioralAttributes = responseData.behavioralAttributes;
        const defaultBehavioralAttributesString =
          defaultBehavioralAttributes.join(':');
        const skills: Skill[] = responseData.mandatorySkills.map(
          (skill: string) => ({
            skill,
            weight: 1,
            weightPercent: 0,
          }),
        );
        const jobTitleScore: Skill[] = responseData.jobTitle?.jobTitle
          ? [
            {
              skill: responseData.jobTitle.jobTitle,
              weight: 10,
              weightPercent: 0,
            },
          ]
          : [];
        const behavioralAttributes: Skill[] =
          responseData.behavioralAttributes.map((attribute: string) => ({
            skill: attribute,
            weight: 10,
            weightPercent: 0,
          }));

        setAiScoreWeights(responseData?.aiScoreWeights || {});
        setExtractedSkill(skills);
        const skillWeights = skills.map(({ skill, weight }) => ({
          skill,
          weight,
        }));
        setExtractedJobTitles(jobTitleScore);
        setExtractedBehavioralScore(behavioralAttributes);
        setBehavioralAttr(defaultBehavioralAttributesString);
        setDefaultSkills(mandatorySkills);
        setJobTitleInput(jobTitle);
        setSkillsInput(mandatorySkillsString);

        if (mandatorySkills.length > 0) {
          setSkillValue([skillValue[0], mandatorySkills.length]);
        } else {
          setSkillValue([skillValue[0], 10]);
        }

        // ONLY set searchObject ONCE with all the data
        if (
          mandatorySkillsString !== '' ||
          jobTitle !== '' ||
          defaultBehavioralAttributesString !== ''
        ) {
          const updatedSearchObject: JobApplicantSearchRequest = {
            ...searchObject,
            additional_skills: {
              skill_weightage: false,
              similar: similarToggle,
              skills: skillWeights.length === 0 ? [] : skillWeights,
            },
            job_title: {
              similar: false,
              job_titles: [jobTitle],
            },
            behavioral_attributes: {
              similar: false,
              behavioral_attributes:
                defaultBehavioralAttributesString === ''
                  ? []
                  : defaultBehavioralAttributesString.split(':'),
            },
            applicant_status: statusValue,
          };

          // Sync with currentTab
          if (currentTab) {
            const config = tabConfig[currentTab as keyof typeof tabConfig];
            if (config) {
              updatedSearchObject.applicant_status = config.applicantStatus;
              setStatus(config.status);
            }
          }

          filtersReadyRef.current = true;
          setSearchObject(updatedSearchObject);
          setSearchCSVObject({
            ...searchCSVObject,
            ...updatedSearchObject,
          });
        } else {
          // No skills/jobTitle to set, still mark filters ready to allow 2d-graph with defaults
          if (!filtersReadyRef.current) {
            filtersReadyRef.current = true;
            // Sanitize empty job_titles [''] -> [] before triggering fetch
            setSearchObject((prev) => ({
              ...prev,
              job_title: { ...prev.job_title, job_titles: (prev.job_title.job_titles || []).filter((v: string) => v && v.trim() !== '') },
            }));
          }
        }
      } catch (error) {
        // Ensure gate is opened even on error to avoid stuck loading
        if (!filtersReadyRef.current) {
          filtersReadyRef.current = true;
          setSearchObject((prev) => ({
            ...prev,
            job_title: { ...prev.job_title, job_titles: (prev.job_title.job_titles || []).filter((v: string) => v && v.trim() !== '') },
          }));
        }
        toast.error(
          `Error fetching job descriptions: ${
            error instanceof Error ? error.message : String(error)
          }`,
          {
            position: 'bottom-center',
          },
        );
      }
    };

    const getFilters = async () => {
      // Keep loading true during filter -> 2d-graph chain for smooth UX
      setLoading(true);
      try {
        const response = await getFilter(id);
        if (!response.ok) {
          throw new Error('Error Fetching Assessment');
        }

        const data = await response.json();
        const defaultBehaviorialAttributes =
          data.behavioralAttributes.behavioralAttributes;
        const defaultBehaviorialAttributesStrings =
          defaultBehaviorialAttributes.join(':');
        const mandatoryJobTitle = data.jobTitle.jobTitles;
        const keyword = data.keywords.keywords;
        const mandatoryJobTitleString = mandatoryJobTitle.join(':');
        const keywordString = keyword.join(':');

        const skills: Skill[] = data.additionalSkills.skills.map(
          (s: { skill: string; weight: number }) => ({
            skill: s.skill,
            weight: s.weight,
            weightPercent: 0,
          }),
        );

        const jobTitles: Skill[] = data.jobTitle.jobTitles.map(
          (jobTitle: string) => ({
            skill: jobTitle,
            weight: 10,
            weightPercent: 0,
          }),
        );

        const keywords: Skill[] = data.keywords.keywords.map(
          (k: { keyword: string; weight: number }) => ({
            skill: k.keyword,
            weight: k.weight,
            weightPercent: 0,
          }),
        );

        const behavioralAttributes: Skill[] =
          data.behavioralAttributes.behavioralAttributes.map(
            (attribute: string) => ({
              skill: attribute,
              weight: 10,
              weightPercent: 0,
            }),
          );

        const transformedData: JobApplicantSearchRequest = normalizeSearchRequest(data);

        // NEW: Synchronize with currentTab BEFORE setting searchObject
        if (currentTab) {
          const config = tabConfig[currentTab as keyof typeof tabConfig];
          if (config) {
            // If the saved filters don't match the active tab's mandatory status, override them
            // This prevents the double-fetch where searchObject is set then immediately reset by the tab sync effect
            const currentStatusStr = JSON.stringify(
              data.applicant_status?.sort() ||
                data.applicantStatus?.sort() ||
                [],
            );
            const tabStatusStr = JSON.stringify(
              [...config.applicantStatus].sort(),
            );

            if (currentStatusStr !== tabStatusStr) {
              transformedData.applicant_status = config.applicantStatus;
              setStatus(config.status);
            }
          }
        }

        const transformedDataCSV: JobApplicantCSV = {
          ...transformedData,
          skills: { min: 0, max: 100 },
          experience: { min: 0, max: 100 },
        };

        const jobSkillsList: string[] = data.additionalSkills.skills.map(
          (s: { skill: string; weight: number }) => s.skill,
        );

        // Set all state updates
        setWeightingEnabled(
          data.additional_skills?.skill_weightage ??
            data.additionalSkills?.skillWeightage,
        );
        // Always start in Skill Count mode (user can switch via toggle)
        setUseSkillPercentage(false);
        setSimilarToggle(
          data.additional_skills?.similar ?? data.additionalSkills?.similar,
        );
        setKeywordWeightage(
          data.keywords?.keyword_weightage ?? data.keywords?.keywordWeightage,
        );
        setKeywordToggle(data.keywords?.similar ?? data.keywords?.similar);
        setSimilarBehavior(
          data.behavioral_attributes?.similar ??
            data.behavioralAttributes?.similar,
        );
        setSimilarJobTitle(data.job_title?.similar ?? data.jobTitle?.similar);
        setExtractedBehavioralScore(behavioralAttributes);
        setExtractedJobTitles(jobTitles);
        setExtractedSkill(skills);
        setExtractedKeywords(keywords);
        setBehavioralAttr(defaultBehaviorialAttributesStrings);
        setGenderList(data.gender);
        setAgeToggle(data.age?.include_unknown ?? data.age?.includeUnknown);
        setDefaultSkills(jobSkillsList);
        setDefaultSkillsCol(jobSkillsList);
        setJobTitleInput(mandatoryJobTitleString);
        setStatusList(data.applicant_status || data.applicantStatus);
        setSimilarKeyword(keywordString);

        // ONLY set searchObject ONCE at the end
        // Mark filters ready BEFORE setting searchObject so debounced effect can fire exactly once
        // Use microtask to ensure ref is set before React batches the state update
        filtersReadyRef.current = true;
        setSearchObject(transformedData);
        setSearchCSVObject(transformedDataCSV);

        if (jobSkillsList.length > 0) {
          setSkillValue([skillValue[0], skills.length]);
          setSkillLength(jobSkillsList.length);
        } else {
          setSkillValue([skillValue[0], 10]);
        }
      } catch (error) {
        // If getFilters fails, fallback to fetchJobDescriptionDetails
        await fetchJobDescriptionDetails();
        // fetchJobDescriptionDetails will set filtersReadyRef when it succeeds
        if (!filtersReadyRef.current) {
          filtersReadyRef.current = true;
        }
      }
    };

    getFilters();

    // Abort any pending 2d-graph on unmount or id change
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [id]);

  const tabConfig = useMemo(
      () => ({
        'All Applicants': {
          status: [
            'New',
            'Suitable',
            'Outstanding',
            'ShortList',
            'Unsuccessful',
            'Auto Assess',
            ...(jobOpeningElements?.includes('Psychometric') ? ['Psychometric'] : []),
            ...(jobOpeningElements?.includes('Wellness') ? ['Wellness'] : []),
          ],
          applicantStatus: [] as string[],
        },
        'New Applicants': {
          status: ['New'],
          applicantStatus: ['New'] as string[],
        },
        'Suitable Applicants': {
          status: ['Suitable'],
          applicantStatus: ['Suitable'] as string[],
        },
        'Outstanding Applicants': {
          status: ['Outstanding'],
          applicantStatus: ['Outstanding'] as string[],
        },
        'Assessment Applicants': {
          status: ['Auto Assess'],
          applicantStatus: ['Auto-Assessment'] as string[],
        },
        'Unsuccessful Applicants': {
          status: ['Unsuccessful'],
          applicantStatus: [
            'Unsuccessful-Closed',
            'Unsuccessful-Pending',
          ] as string[],
        },
        'ShortList Applicants': {
          status: ['ShortList'],
          applicantStatus: ['Shortlisted'] as string[],
        },
        ...(jobOpeningElements?.includes('Psychometric') && {
          Psychometric: {
            status: ['Psychometric'],
            applicantStatus: ['Psychometric'] as string[],
          },
        }),
        ...(jobOpeningElements?.includes('Wellness') && {
          Wellness: {
            status: ['Wellness'],
            applicantStatus: ['Wellness'] as string[],
          },
        }),
      }),
      [subscriptions?.hierTalentPsychometricFaxtor, jobOpeningElements],
  );

  useEffect(() => {
    // Guard: Skip if currentTab is not set or filters not ready yet
    // Do not depend on searchObject.applicant_status to avoid double-fetch loop
    if (!currentTab || !filtersReadyRef.current) {
      return;
    }

    const config = tabConfig[currentTab as keyof typeof tabConfig];

    // Guard: Skip if config doesn't exist
    if (!config) {
      return;
    }

    // Use functional update to compare inside updater and avoid extra renders
    setSearchObject((prev) => {
      const currentStatusStr = JSON.stringify(
        [...(prev.applicant_status || [])].sort(),
      );
      const newStatusStr = JSON.stringify([...config.applicantStatus].sort());
      if (currentStatusStr === newStatusStr) {
        return prev; // No change, avoid triggering 2d-graph
      }
      return {
        ...prev,
        applicant_status: config.applicantStatus,
      };
    });
    setSearchCSVObject((prev) => {
      const currentStatusStr = JSON.stringify(
        [...(prev.applicant_status || [])].sort(),
      );
      const newStatusStr = JSON.stringify([...config.applicantStatus].sort());
      if (currentStatusStr === newStatusStr) {
        return prev;
      }
      return {
        ...prev,
        applicant_status: config.applicantStatus,
      };
    });
    setStatus(config.status);
  }, [currentTab, tabConfig]);

  useEffect(() => {
    if (!filtersReadyRef.current) {
      return;
    }
    // Avoid no-op updates that would trigger an extra 2d-graph call
    setSearchObject((prev) => {
      const prevStr = JSON.stringify(prev.prequal_filter || null);
      const nextStr = JSON.stringify(prequal_filter || null);
      if (prevStr === nextStr) return prev;
      return {
        ...prev,
        prequal_filter: prequal_filter,
      };
    });
    setSearchCSVObject((prev) => {
      const prevStr = JSON.stringify(prev.prequal_filter || null);
      const nextStr = JSON.stringify(prequal_filter || null);
      if (prevStr === nextStr) return prev;
      return {
        ...prev,
        prequal_filter: prequal_filter,
      };
    });
  }, [prequal_filter]);
  const sidebarButtons = useMemo(
      () => [
        {
          label: 'All Applicants',
          disabled: false,
          Icon: UsersIcons,
        },
        {
          label: 'New Applicants',
          disabled: false,
          Icon: NewUserIcons,
        },
        {
          label: 'Suitable Applicants',
          disabled: false,
          Icon: CircleCheckIcons,
        },
        {
          label: 'Outstanding Applicants',
          disabled: false,
          Icon: StarIcons,
        },
        {
          label: 'Assessment Applicants',
          disabled: false,
          Icon: ClipBoardIcons,
        },
        {
          label: 'Unsuccessful Applicants',
          disabled: false,
          Icon: ShieldAlertIcons,
        },
        ...(jobOpeningElements?.includes('Psychometric')
            ? [
              {
                label: 'Psychometric',
                disabled: false,
                Icon: ListCheckIcons,
              },
            ]
            : []),
        ...(jobOpeningElements?.includes('Wellness')
            ? [
              {
                label: 'Wellness',
                disabled: false,
                Icon: ListCheckIcons,
              },
            ]
            : []),
        {
          label: 'ShortList Applicants',
          disabled: false,
          Icon: ListCheckIcons,
        },
      ],
      [jobOpeningElements, subscriptions?.hierTalentPsychometricFaxtor],
  );
  const handleStatusChange = useCallback(
    (activeLabel: string) => {
      setCurrentTab(activeLabel);

      // Update URL to include status parameter
      const searchParams = new URLSearchParams(window.location.search);
      searchParams.set('tab', 'applicant-analysis');
      searchParams.set('applicantTab', activeTab.toString());

      if (activeLabel === 'All Applicants') {
        searchParams.delete('status'); // Remove status param for default
      } else {
        searchParams.set('status', convertToUrlFormat(activeLabel));
      }

      const newUrl = `${window.location.pathname}?${searchParams.toString()}`;
      window.history.pushState(null, '', newUrl);
    },
    [activeTab],
  );

  const handleRefreshData = useCallback(async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;
    setLoading(true);
    try {
      await fetchJobApplicants(controller.signal, searchObject);
    } catch (error: any) {
      if (error?.name === 'AbortError' || controller.signal.aborted) return;
      console.error('Error refreshing data:', error);
      toast.error('Failed to refresh data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [fetchJobApplicants, setLoading]);

  return {
    activeTab,
    updateActiveTab,
    tabTitles,
    filteredCandidates,
    loading,
    candidates,
    currentTab,
    globalSearchTerm,
    setIsAnalyticalFilter,
    isAnalyticalFilter,
    clearSelection,
    setIsColumnConfigurationOpen,
    handleExportClick,
    setShowSearchPanel,
    showSearchPanel,
    handleSendEmailToSelected,
    handleSendEmailToSingleApplicant, // New function for profile dialog
    setView3D,
    view3D,
    setSvgActiveTab,
    setIsToolbarExpanded,
    isToolbarExpanded,
    selectedApplicantsForEmail,
    viewMode,
    setViewMode,
    showHeadshots,
    setShowHeadshots,
    selectAllUnsuccessful,
    setGlobalSearchTerm,
    svgActiveTab,
    extractedSkill,
    extractedBehavioralScore,
    extractedJobTitles,
    extractedKeywords,
    setTabTitles,
    setActiveTab: updateActiveTab,
    handleCloseTab,
    markLines,
    setMarkLines,
    nineBox,
    setNineBox,
    selectedCandidates,
    setSelectedCandidates,
    handleApplicantSelection,
    isApplicantSelected,
    setExactKeyword,
    exactKeyword,
    setSearchCSVObject,
    searchCSVObject,
    skillValue,
    setSkillValue,
    experienceValue,
    setExperienceValue,
    scatter,
    setScatter,
    setKeywordList,
    useSkillPercentage,
    handleCandidateClickFromChart,
    colorList,
    projection,
    boxWidthDepth,
    maxLegion,
    isSeriesModal,
    setIsSeriesModal,
    seriesCandidates,
    setSeriesCandidates,
    columns,
    setColumns,
    defaultSkills,
    getColumns,
    aiScoreWeights,
    handleMoveToUnsuccessful,
    handleMoveToUnsuccessfulFromOpen, // New function for moving from open to unsuccessful pending
    handleBulkStatusChange,
    getApplicantInitials,
    handleViewApplicantProfile,
    handleStatusChange,
    handleRefreshData,
    isColumnConfigurationOpen,
    defaultSkillsCol,
    setStatus,
    statusValue,
    minMaxAge,
    setMinMaxAge,
    jobTitleInput,
    setJobTitleInput,
    behavioralAttr,
    setBehavioralAttr,
    setSimilarKeyword,
    similarKeyword,
    genderList,
    setGenderList,
    statusList,
    setStatusList,
    similarToggle,
    setSimilarToggle,
    ageToggle,
    setAgeToggle,
    keywordList,
    id,
    searchObject,
    setSearchObject,
    setExtractedSkill,
    setExtractedJobTitles,
    setExtractedKeywords,
    setExtractedBehavioralScore,
    weightingEnabled,
    setWeightingEnabled,
    keywordWeightage,
    setKeywordWeightage,
    similarBehavior,
    setSimilarBehavior,
    similarJobTitle,
    setSimilarJobTitle,
    keywordToggle,
    setKeywordToggle,
    setUseSkillPercentage,
    setPrequalFilter,
    prequal_filter,
    isAnalytical3D,
    setIsAnalytical3D,
    setColorList,
    setProjection,
    setBoxWidthDepth,
    isProfileModalOpen,
    setIsProfileModalOpen,
    profileModalCandidates,
    profileModalInitialIndex,
    profileModalTileName,
    normalizeStatusDisplay,
    setProfileModalCandidates,
    setProfileModalInitialIndex,
    setProfileModalTileName,
    isEmailConfirmationModalOpen,
    setIsEmailConfirmationModalOpen,
    isEmailSending,
    handleConfirmSendEmail,
    handleCloseEmailConfirmationModal,
    setSelectedApplicantsForEmail,
  };
};
